import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LogOut, ExternalLink } from "lucide-react";
import { http } from "@/lib/api";
import { Logo } from "@/components/site/Logo";
import { ResourceManager } from "@/components/admin/ResourceManager";
import { SettingsForm } from "@/components/admin/SettingsForm";
import { TrackingManager } from "@/components/admin/TrackingManager";

const PAY = [{ key: "price", label: "Preço exibido (ex: R$ 49,90)" }];

const TABS = {
  services: {
    label: "Serviços",
    titleKey: "title",
    defaults: { title: "", category: "", description: "", price: "", deadline_days: 4, image_url: "", gallery: [], mp_link: "", external_link: "", active: true },
    fields: [{ key: "title", label: "Título" }, { key: "category", label: "Categoria" }, { key: "description", label: "Descrição", type: "textarea" }, ...PAY, { key: "deadline_days", label: "Prazo de entrega (dias úteis)", type: "number" }, { key: "image_url", label: "Imagem", type: "image" }, { key: "gallery", label: "Prévias do serviço (até 5 imagens)", type: "gallery", max: 5 }, { key: "order", label: "Ordem", type: "number" }, { key: "active", label: "Ativo", type: "bool" }],
  },
  packs: {
    label: "Pack Gospel",
    titleKey: "name",
    defaults: { name: "Pack Gospel ", version: "", tag: "", description: "", features: [], price: "", price_value: 0, image_url: "", gallery: [], mp_link: "", external_link: "", featured: false, active: true },
    fields: [{ key: "name", label: "Nome" }, { key: "version", label: "Versão (ex: 5.0)" }, { key: "tag", label: "Selo (ex: Mais vendido)" }, { key: "description", label: "Descrição", type: "textarea" }, { key: "features", label: "O que inclui", type: "lines" }, ...PAY, { key: "deadline_days", label: "Prazo de entrega (dias úteis, 0 = mesmo dia)", type: "number" }, { key: "image_url", label: "Imagem de capa", type: "image" }, { key: "gallery", label: "Prévias das artes (galeria)", type: "gallery" }, { key: "order", label: "Ordem", type: "number" }, { key: "featured", label: "Destaque", type: "bool" }, { key: "active", label: "Ativo", type: "bool" }],
  },
  portfolio: {
    label: "Portfólio",
    titleKey: "title",
    defaults: { title: "", category: "", image_url: "", size: "md" },
    fields: [{ key: "title", label: "Título" }, { key: "category", label: "Categoria (vira filtro)" }, { key: "image_url", label: "Imagem", type: "image" }, { key: "size", label: "Tamanho no grid", type: "select", options: [["md", "Quadrado"], ["tall", "Alto"], ["wide", "Largo"]] }, { key: "order", label: "Ordem", type: "number" }],
  },
};

export default function Admin() {
  const nav = useNavigate();
  const [tab, setTab] = useState("tracking");
  const [user, setUser] = useState(null);
  useEffect(() => {
    if (!localStorage.getItem("sd_token")) {
      nav("/admin/login");
      return undefined;
    }
    http.get("/auth/me").then((r) => setUser(r.data)).catch(() => {
      localStorage.removeItem("sd_token");
      nav("/admin/login");
    });
    return undefined;
  }, [nav]);
  const logout = () => {
    localStorage.removeItem("sd_token");
    nav("/admin/login");
  };
  if (!user) return <div className="min-h-screen bg-[#041214]" />;
  const cfg = TABS[tab];
  return (
    <div className="min-h-screen bg-[#041214] text-teal-50" data-testid="admin-dashboard">
      <header className="flex items-center justify-between border-b border-[#163D43] px-5 py-4 md:px-10">
        <Logo />
        <div className="flex items-center gap-4 text-sm">
          <Link to="/" target="_blank" className="hidden items-center gap-1 text-teal-200 hover:text-[#2DD4BF] sm:inline-flex" data-testid="admin-view-site"><ExternalLink className="h-4 w-4" /> Ver site</Link>
          <button onClick={logout} className="inline-flex items-center gap-1 text-teal-200 hover:text-[#2DD4BF]" data-testid="admin-logout"><LogOut className="h-4 w-4" /> Sair</button>
        </div>
      </header>
      <div className="mx-auto grid max-w-6xl gap-8 px-5 py-10 md:grid-cols-4 md:px-10">
        <nav className="flex gap-2 overflow-x-auto md:flex-col">
          {[["tracking", "Pedidos"], ...Object.entries(TABS).map(([k, v]) => [k, v.label]), ["settings", "Pix & Contato"]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`} className={`whitespace-nowrap rounded-md px-4 py-2.5 text-left text-sm transition-colors ${tab === k ? "bg-[#2DD4BF] font-semibold text-[#041214]" : "text-teal-100 hover:bg-[#0B2326]"}`}>{l}</button>
          ))}
        </nav>
        <main className="md:col-span-3">
          <p className="font-mono-x text-xs uppercase tracking-[0.2em] text-teal-300">Olá, {user.name || "Admin"}</p>
          <h1 className="font-display mb-8 mt-2 text-3xl font-semibold">{cfg?.label || (tab === "tracking" ? "Pedidos" : "Pix & Contato")}</h1>
          {cfg ? <ResourceManager key={tab} name={tab} {...cfg} /> : tab === "tracking" ? <TrackingManager /> : <SettingsForm />}
        </main>
      </div>
    </div>
  );
}
